"use client";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import { AuthService } from "@/components/service/auth/auth.service";
import {
  parseJwt,
  storeUserData,
  getUserData,
  clearAuthStorage,
  hasModule as checkModule,
  hasPermissionWithFallback,
} from "@/lib/auth/token";
import { useSessionTimer } from "@/hooks/auth/useSessionTimer";

type User = {
  id: string;
  role: string;
  modules: string[];
  permissions: any[];
};

type AuthContextType = {
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: () => boolean;
  scheduleTokenExpiration: () => void;
  hasModule: (moduleKey: string) => boolean;
  hasPermission: (perm: string | number, applicationType?: string) => boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const [user, setUser] = useState<User | null>(null);

  // Initialize session timer hook
  const { scheduleTokenExpiration, clearTimer } = useSessionTimer(() => {
    logout();
    router.push("/login");
  });

  // Initialize user from token on mount
  useEffect(() => {
    const token = AuthService.getToken();
    if (!token) {
      logout();
      return;
    }

    const userData = parseJwt(token);
    const applicationTypes =
      userData.rolePermissions?.map((rp: any) => rp.applicationType) || [];

    const userState = {
      id: userData.id,
      role: userData.role,
      modules: applicationTypes,
      permissions: userData.rolePermissions,
    };

    setUser(userState);
    storeUserData(userState);
    scheduleTokenExpiration();

    return () => {
      clearTimer();
    };
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await AuthService.userLogin(username, password);
      if (response.success) {
        const userData = parseJwt(response.data.accessToken);
        console.log("Login successful, user data:", userData);

        // Extract application types from rolePermissions
        const applicationTypes =
          userData.rolePermissions?.map((rp: any) => rp.applicationType) || [];

        console.group("[RBAC Debug - Login]");
        console.log("Raw rolePermissions:", userData.rolePermissions);
        console.log("Extracted modules:", applicationTypes);
        console.groupEnd();

        const userState = {
          id: userData.id,
          role: userData.role,
          modules: applicationTypes,
          permissions: userData.rolePermissions,
        };

        setUser(userState);
        storeUserData(userState);
        scheduleTokenExpiration();
        router.push("/dashboard");
      }
    } catch (error) {
      console.error("AuthContext login error:", error);
      throw error;
    }
  };

  // Check if user has access to a given module key
  const hasModule = (moduleKey: string) => {
    return checkModule(user?.modules, moduleKey);
  };

  // Check if user has a specific permission (by id or name)
  const hasPermission = (perm: string | number, applicationType?: string) => {
    return hasPermissionWithFallback(user?.permissions, perm, applicationType);
  };

  const logout = () => {
    clearTimer();
    AuthService.clearToken();
    clearAuthStorage();
    router.push("/login");
  };

  const isAuthenticated = () => {
    return !!AuthService.getToken();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated,
        scheduleTokenExpiration,
        hasModule,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
